import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewChild,
  computed,
  inject,
  input,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Button, ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { ProgressBarModule } from 'primeng/progressbar';
import { Sidebar, SidebarModule } from 'primeng/sidebar';

export interface HeaderButton extends Partial<Button> {
  visible?: boolean;
  action?: () => void;
  tooltip?: string;
}

@Component({
  selector: 'kon-sidebar-layout',
  standalone: true,
  imports: [
    CommonModule,
    SidebarModule,
    ProgressBarModule,
    ButtonModule,
    MenuModule,
  ],
  template: `
    <p-sidebar
      #sidebarRef
      [visible]="true"
      (visibleChange)="changeVisibility($event)"
      [style]="style"
      (onShow)="onShow()"
      [modal]="true"
      [appendTo]="'body'"
      position="right"
    >
      <ng-template pTemplate="headless">
        <p-progressBar
          *ngIf="loading()"
          mode="indeterminate"
          [style]="{ height: '4px', width: '100%' }"
          [ngClass]="{ loading: loading }"
        ></p-progressBar>

        <div
          class="flex flex-wrap items-center py-2 px-3 border-b border-gray-100"
        >
          <div
            class="flex-grow flex items-center justify-between px-3 order-first"
          >
            <h2>
              {{ title }}
            </h2>
          </div>
          <p-button
            *ngFor="let button of visibleButtons()"
            [label]="button.label"
            [icon]="button.icon"
            [styleClass]="button.styleClass ?? 'mr-4'"
            [disabled]="button.disabled"
            [iconPos]="button.iconPos ?? 'right'"
            (onClick)="button.action?.()"
            [loading]="button.loading ?? false"
            [outlined]="button.outlined ?? false"
            [plain]="button.plain ?? false"
            [rounded]="button.rounded ?? false"
            [severity]="button.severity"
            [size]="button.size"
            [style]="button.style"
            [badge]="button.badge"
            [badgeClass]="button.badgeClass"
            [ariaLabel]="button.ariaLabel"
            [pTooltip]="button.tooltip"
          />

          <p-button
            *ngIf="visibleActions()?.length ?? 0 > 0"
            [icon]="'pi pi-ellipsis-v'"
            styleClass="p-button-sm p-button-text p-button-secondary p-button-rounded"
            (click)="contextMenu.toggle($event)"
          />

          <p-menu
            #contextMenu
            [model]="visibleActions()"
            [popup]="true"
            [appendTo]="'body'"
          />

          <button
            pButton
            icon="pi pi-times"
            class="p-button-rounded p-button-text p-button-secondary"
            (click)="changeVisibility(false)"
          ></button>

          <div class="order-last sm:order-first w-full sm:w-auto sm:mr-2">
            <ng-content select="[header]"></ng-content>
          </div>
        </div>

        <div class="h-full overflow-auto px-3">
          <div [ngClass]="contentClasses">
            <ng-content></ng-content>
          </div>
        </div>
      </ng-template>
    </p-sidebar>
  `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarLayoutComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  @Input() contentClasses = 'p-3';

  private _title?: string;
  @Input() set title(value: string | undefined) {
    this._title = value;
  }
  get title() {
    return this._title ?? this.route.snapshot.title;
  }

  private _style: Record<string, unknown> = {
    width: '500px',
    'max-width': '95%',
  };
  @Input() set style(value: Record<string, unknown>) {
    this._style = {
      ...this._style,
      ...value,
    };
  }
  get style() {
    return this._style;
  }

  @Input() returnRoute: string | unknown[] | null = null;

  loading = input<boolean | undefined | null>(false);

  actions = input<MenuItem[]>();

  buttons = input<HeaderButton[]>();

  @ViewChild('sidebarRef') sidebarRef?: Sidebar;

  changeVisibility(visible: boolean) {
    if (!visible) {
      // If rendered inside a named (auxiliary) outlet, clear that outlet instead
      if (this.route.outlet !== 'primary') {
        this.router
          .navigate([{ outlets: { [this.route.outlet]: null } }])
          .then((success) => {
            if (!success) {
              this.sidebarRef?.enableModality();
            }
          });
        return;
      }

      let navRoute: string | unknown[] | null = this.returnRoute;
      let relativeTo: ActivatedRoute | null | undefined = undefined;
      if (!navRoute) {
        // get first parent route which has component defined (parent can be componentless route)
        let parent = this.route.parent;
        while (parent && !parent.component) {
          parent = parent.parent;
        }
        if (parent) {
          navRoute = ['.'];
          relativeTo = parent;
        } else {
          // this should never happen but just in case
          navRoute = ['..'];
        }
      }

      this.router.navigate(['.'], { relativeTo }).then((success) => {
        if (!success) {
          // guard blocked navigation - show modal backdrop again
          this.sidebarRef?.enableModality();
        }
      });
    }
  }

  onShow() {
    if (this.sidebarRef?.mask) {
      this.sidebarRef.mask.style.zIndex = (
        parseInt(this.sidebarRef?.container?.style?.zIndex ?? '0') - 1
      ).toString();
    }
  }

  visibleActions = computed(() =>
    this.actions()
      ?.filter((a) => a.visible)
      .map(
        (a) => ({ ...a, disabled: a.disabled || !!this.loading() } as MenuItem),
      ),
  );

  visibleButtons = computed(() =>
    this.buttons()
      ?.filter((a) => a.visible !== false)
      ?.map(
        (a) =>
          ({ ...a, disabled: a.disabled || !!this.loading() } as HeaderButton),
      ),
  );
}
